import { type ChangeEvent, useCallback, useLayoutEffect, useMemo, useRef } from 'react';

import { Country } from '@/constants/countries-phones';
import {
  caretIndexForDigitCount,
  clampLocalPhoneDigits,
  countDigitsBefore,
  formatLocalPhoneNumber,
  parsePhoneNumber,
} from '@/utils/parse-phone-number';

export interface PhoneNumberFieldChange {
  digits: string;
  nationalNumber: string;
  country?: Country;
}

export interface UsePhoneNumberFieldParams {
  digits: string;
  iso2: string | null | undefined;
  onValueChange: (_change: PhoneNumberFieldChange) => void;
}

export interface UsePhoneNumberFieldResult {
  displayValue: string;
  nationalNumber: string;
  onChange: (_event: ChangeEvent<HTMLInputElement>) => void;
}

interface PendingCaret {
  element: HTMLInputElement;
  digitsBeforeCaret: number;
}

export const usePhoneNumberField = ({
  digits,
  iso2,
  onValueChange,
}: UsePhoneNumberFieldParams): UsePhoneNumberFieldResult => {
  const formatted = useMemo(() => formatLocalPhoneNumber(digits, iso2), [digits, iso2]);
  const pendingCaretRef = useRef<PendingCaret | null>(null);

  const caretForDigits = useCallback(
    (digitsBeforeCaret: number) => caretIndexForDigitCount(formatted.display, digitsBeforeCaret),
    [formatted.display],
  );

  useLayoutEffect(() => {
    const pending = pendingCaretRef.current;
    if (!pending) return;
    pendingCaretRef.current = null;
    const caret = caretForDigits(pending.digitsBeforeCaret);
    pending.element.setSelectionRange(caret, caret);
  }, [caretForDigits]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const element = event.currentTarget;
      const rawValue = element.value;
      const rawCaret = element.selectionStart ?? rawValue.length;

      if (rawValue.trim().startsWith('+')) {
        const parsed = parsePhoneNumber(rawValue.replace(/[^\d+]/g, ''));
        if (parsed.country) {
          const pasted = clampLocalPhoneDigits(
            parsed.localNumber.replace(/\D/g, ''),
            parsed.country.iso2,
          );
          onValueChange({
            digits: pasted,
            nationalNumber: formatLocalPhoneNumber(pasted, parsed.country.iso2).nationalNumber,
            country: parsed.country,
          });
          return;
        }
      }

      const nextDigits = clampLocalPhoneDigits(rawValue.replace(/\D/g, ''), iso2);
      const digitsBeforeCaret = Math.min(countDigitsBefore(rawValue, rawCaret), nextDigits.length);

      if (nextDigits === digits) {
        // React reverts the controlled value without re-rendering when the digits are unchanged
        // (deleting a separator), so the layout effect never runs and the caret would land at the
        // end. Restore it after that revert.
        const caret = caretForDigits(digitsBeforeCaret);
        requestAnimationFrame(() => element.setSelectionRange(caret, caret));
        return;
      }

      pendingCaretRef.current = { element, digitsBeforeCaret };
      onValueChange({
        digits: nextDigits,
        nationalNumber: formatLocalPhoneNumber(nextDigits, iso2).nationalNumber,
      });
    },
    [caretForDigits, digits, iso2, onValueChange],
  );

  return { displayValue: formatted.display, nationalNumber: formatted.nationalNumber, onChange };
};
